// @flow
import axios from 'lib/defaultClient';

const SESSION_KEY = '__tracking_session__';

type TrackingSession = {
  sessionId: string,
  startedAt: string,
};

type PageInfo = {
  path: string,
  title: string,
  referrer: string,
};

type EventProperties = {
  [key: string]: any,
};

const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getSessionStorage = () => {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage;
};

const getSession = (): ?TrackingSession => {
  const ss = getSessionStorage();
  if (!ss) return null;
  try {
    const raw = ss.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const setSession = (session: TrackingSession): void => {
  const ss = getSessionStorage();
  if (!ss) return;
  ss.setItem(SESSION_KEY, JSON.stringify(session));
};

const getPageInfo = (location?: { pathname: string, search: string }): PageInfo => {
  if (typeof window === 'undefined') {
    return { path: '', title: '', referrer: '' };
  }
  const path = location
    ? location.pathname + location.search
    : window.location.pathname + window.location.search;
  return {
    path,
    title: document.title || '',
    referrer: document.referrer || '',
  };
};

let pendingSessionPromise: ?Promise<?string> = null;

const createSession = async (sessionId: string, startedAt: string): Promise<boolean> => {
  try {
    const page = getPageInfo();
    await axios.post('/tracking/sessions', {
      sessionId,
      startedAt,
      entryPath: page.path,
      referrer: page.referrer,
    });
    return true;
  } catch (e) {
    return false;
  }
};

export const ensureSession = async (): Promise<?string> => {
  const session = getSession();
  if (session) return session.sessionId;

  if (pendingSessionPromise) return pendingSessionPromise;

  pendingSessionPromise = (async () => {
    const sessionId = generateUUID();
    const startedAt = new Date().toISOString();

    const success = await createSession(sessionId, startedAt);
    if (success) {
      setSession({ sessionId, startedAt });
      return sessionId;
    }
    return null;
  })();

  try {
    return await pendingSessionPromise;
  } finally {
    pendingSessionPromise = null;
  }
};

export const send = async (
  eventName: string,
  properties: EventProperties = {},
): Promise<void> => {
  const sessionId = await ensureSession();
  if (!sessionId) return;

  try {
    const page = getPageInfo();
    await axios.post('/tracking/events', {
      eventId: generateUUID(),
      sessionId,
      eventName,
      occurredAt: new Date().toISOString(),
      page,
      properties,
    });
  } catch (e) {
    // fire-and-forget
  }
};

export const reset = (): void => {
  const ss = getSessionStorage();
  if (!ss) return;
  ss.removeItem(SESSION_KEY);
  pendingSessionPromise = null;
};



// WEBPACK FOOTER //
// ./src/lib/tracking/dbTracker.js